import { createSlice } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import {
  AuxPayload,
  FiltersState,
  JEWELRY_SELECTED_TYPE_PAYLOAD,
} from "@utils/constants";

export type ExcludePayload = {
  [key: string]: string[];
};

const initialState = {
  auxPayload: {},
  payload: {},
  filters: {}, // Initial value with updatedTime
  FiltersSet: false,
  sectionName: "",
  updatedTime: "",
  startRangeIndex: {},
  displayTabs: false,
  collectionTabs: [],
  collection: [],
  selectedCollectionOption: null,
  sub_collection: [],
  isResetInProgress: false,
  min_max_Options: {},
  selectedFilterTab: "classic_search",
};

const FALLBACK_VALUE = {};

const searchFiltersSlice = createSlice({
  name: "searchFilters",
  initialState,
  reducers: {
    setFiltersPayload: (state, action) => {
      const { sectionName, data } = action.payload;
      state.auxPayload[sectionName] = {
        ...state.auxPayload?.[sectionName],
        ...data,
      };
      state.updatedTime = new Date().toISOString();
    },
    removeFiltersPayload: (state, action) => {
      const { sectionName, payloadName, key } = action.payload;
      const section = state.auxPayload[sectionName];

      if (payloadName && section) {
        if (key === "argyle" && typeof payloadName === "object") {
          Object.keys(payloadName).forEach(
            (item) => delete section[key]?.[item]
          );
          if (!Object.keys(section[key] || {}).length) delete section[key];
        } else {
          payloadName.forEach((item) => delete section[item]);
        }
        state.auxPayload[sectionName] = { ...section };
      }
    },
    resetFiltersPayload: (state) => {
      state.auxPayload = {};
      state.payload = {};
    },

    resetFiltersPayloadByProduct: (state, action) => {
      const productType = action.payload;
      state.auxPayload = {};

      if (state.payload && state.payload?.[productType]) {
        const newPayload = { ...state.payload };
        delete newPayload[productType];

        state.payload = newPayload;
      }
    },
    resetAuxPayload: (state) => {
      state.auxPayload = {};
    },

    setFiltersData: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.updatedTime = new Date().toISOString();
    },
    setPayloadData: (state, action) => {
      const { data, productType } = action.payload;

      if (!state.payload[productType]) {
        state.payload[productType] = {};
      }

      state.payload[productType] = {
        ...state.payload[productType],
        ...data,
      };
    },
    setAuxPayloadData: (state, action) => {
      state.auxPayload = { ...state.auxPayload, ...action.payload };
    },

    setFiltersSet: (state) => {
      state.FiltersSet = true;
    },
    resetFiltersSet: (state) => {
      state.FiltersSet = false;
    },
    setSectionName: (state, action) => {
      state.sectionName = action.payload;
    },
    setUpdatedTimeSet: (state) => {
      state.updatedTime = new Date().toISOString();
    },
    resetUpdatedTime: (state) => {
      state.updatedTime = "";
    },
    resetJewelryFiltersPayload: (state: FiltersState, action) => {
      const preservedData: AuxPayload = {};
      const setPreservedData = action.payload;
      Object.keys(state.auxPayload).forEach((category) => {
        if (
          typeof state.auxPayload[category] === "object" &&
          state.auxPayload[category] !== null
        ) {
          if (
            JEWELRY_SELECTED_TYPE_PAYLOAD in state.auxPayload[category] &&
            setPreservedData
          ) {
            preservedData[category] = {
              [JEWELRY_SELECTED_TYPE_PAYLOAD]:
                state.auxPayload[category][JEWELRY_SELECTED_TYPE_PAYLOAD],
            };
          }
        }
      });

      state.auxPayload = preservedData;
      state.payload = preservedData;
    },
    setStartRangeIndex: (state, action) => {
      const { sectionName, data } = action.payload;

      state.startRangeIndex = {
        ...state.startRangeIndex,
        [sectionName]: data ?? null,
      };
    },
    setDisplayTabs: (state, action) => {
      state.displayTabs = action.payload;
    },
    setCollectionTabs: (state, action) => {
      state.collectionTabs = action.payload;
    },
    clearCollectionTabs: (state) => {
      state.collectionTabs = [];
      state.displayTabs = false;
    },
    setCollection: (state, action) => {
      const collectionValue = action.payload;
      if (typeof collectionValue === "string") {
        if (collectionValue === "") {
          state.collection = [];
        } else if (!state.collection.includes(collectionValue)) {
          state.collection = [...state.collection, collectionValue];
        }
      } else if (Array.isArray(collectionValue)) {
        state.collection = collectionValue;
      }
    },
    clearCollections: (state) => {
      state.collection = [];
      state.sub_collection = [];
    },
    removeCollection: (state, action) => {
      const collectionToRemove = action.payload;
      state.collection = state.collection.filter(
        (item) => item !== collectionToRemove
      );
    },
    setSelectedCollectionOption: (state, action) => {
      state.selectedCollectionOption = action.payload;
    },
    clearSelectedCollectionOption: (state) => {
      state.selectedCollectionOption = null;
    },
    setSubCollection: (state, action) => {
      const collectionValue = action.payload;
      if (typeof collectionValue === "string") {
        if (collectionValue === "") {
          state.sub_collection = [];
        } else if (!state.sub_collection.includes(collectionValue)) {
          state.sub_collection = [...state.sub_collection, collectionValue];
          if (!state.collection.includes(collectionValue)) {
            state.collection = [...state.collection, collectionValue];
          }
        }
      } else if (Array.isArray(collectionValue)) {
        state.sub_collection = collectionValue;
        collectionValue.forEach((item) => {
          if (!state.collection.includes(item)) {
            state.collection = [...state.collection, item];
          }
        });
      }
    },
    removeSubCollection: (state, action) => {
      const collectionToRemove = action.payload;
      state.sub_collection = state.sub_collection.filter(
        (item) => item !== collectionToRemove
      );
      state.collection = state.collection.filter(
        (item) => item !== collectionToRemove
      );
    },
    applyCollectionSubcategorySelection: (state, action) => {
      const {
        sectionCollections = [],
        selectedCollections = [],
        sectionName,
        payload = {},
        removePayloadKeys = [],
      } = action.payload;

      const sectionSet = new Set(sectionCollections);

      const nextCollection = [
        ...state.collection.filter((c) => !sectionSet.has(c)),
        ...selectedCollections,
      ];
      const collectionChanged =
        nextCollection.length !== state.collection.length ||
        nextCollection.some((c, i) => c !== state.collection[i]);
      if (collectionChanged) {
        state.collection = nextCollection;
      }

      const nextSubCollection = [
        ...state.sub_collection.filter((c) => !sectionSet.has(c)),
        ...selectedCollections,
      ];
      const subCollectionChanged =
        nextSubCollection.length !== state.sub_collection.length ||
        nextSubCollection.some((c, i) => c !== state.sub_collection[i]);
      if (subCollectionChanged) {
        state.sub_collection = nextSubCollection;
      }

      if (selectedCollections.length === 0) {
        const section = state.auxPayload[sectionName];
        if (section && removePayloadKeys.length) {
          removePayloadKeys.forEach((key) => {
            // Section-level jewelry flags — not owned by a subcategory chip.
            if (key === "sub_type" || key === "lab_grown") return;
            delete section[key];
          });
          state.auxPayload[sectionName] = { ...section };
        }
      } else if (sectionName) {
        const existing = state.auxPayload?.[sectionName] || {};
        const next = { ...existing, ...payload };
        if (sectionName === "Lab-Grown Jewelry") {
          next.sub_type = existing.sub_type || payload.sub_type || "labgrown";
          next.lab_grown = true;
        }
        state.auxPayload[sectionName] = next;
        state.updatedTime = new Date().toISOString();
      }
    },
    clearSubCollections: (state) => {
      state.collection = state.collection.filter((item) => {
        return !state.sub_collection.includes(item);
      });
      state.sub_collection = [];
      state.isResetInProgress = true;
    },
    setResetInProgress: (state, action) => {
      state.isResetInProgress = action.payload;
    },
    setMinMaxOptions: (state, action) => {
      state.min_max_Options = action.payload;
    },
    resetMinMaxOptions: (state) => {
      state.min_max_Options = {};
    },
    setSelectedFilterTab: (state, action) => {
      state.selectedFilterTab = action.payload;
    },
    setExcludePayload: (state, action) => {
      const { sectionName, filterKey, values } = action.payload;
      if (!state.auxPayload[sectionName]) {
        state.auxPayload[sectionName] = {};
      }
      if (!state.auxPayload[sectionName]._exclude) {
        state.auxPayload[sectionName]._exclude = {};
      }
      if (values && values.length > 0) {
        state.auxPayload[sectionName]._exclude[filterKey] = values;
      } else {
        delete state.auxPayload[sectionName]._exclude[filterKey];
      }
      state.updatedTime = new Date().toISOString();
    },
    resetExcludePayloadByFilterType: (state, action) => {
      const { sectionName, filterKey } = action.payload;
      if (state.auxPayload[sectionName]?._exclude?.[filterKey]) {
        delete state.auxPayload[sectionName]._exclude[filterKey];
      }
      state.updatedTime = new Date().toISOString();
    },
  },
});

export const {
  setFiltersPayload,
  removeFiltersPayload,
  setFiltersData,
  resetFiltersPayload,
  resetAuxPayload,
  setPayloadData,
  setAuxPayloadData,
  setFiltersSet,
  resetFiltersPayloadByProduct,
  resetFiltersSet,
  setSectionName,
  resetJewelryFiltersPayload,
  setUpdatedTimeSet,
  resetUpdatedTime,
  setStartRangeIndex,
  setCollection,
  clearCollections,
  removeCollection,
  setSelectedCollectionOption,
  clearSelectedCollectionOption,
  setSubCollection,
  removeSubCollection,
  applyCollectionSubcategorySelection,
  clearSubCollections,
  setResetInProgress,
  setDisplayTabs,
  setCollectionTabs,
  clearCollectionTabs,
  setMinMaxOptions,
  resetMinMaxOptions,
  setSelectedFilterTab,
  setExcludePayload,
  resetExcludePayloadByFilterType,
} = searchFiltersSlice.actions;

export default searchFiltersSlice.reducer;

export const useSearchFiltersSlice = () => {
  return useSelector((state) => state.searchFilters);
};

export const useSearchFiltersPayload = (productType) => {
  return useSelector(
    (state) => state.searchFilters?.payload?.[productType] ?? FALLBACK_VALUE
  );
};

export const useSearchFiltersAuxPayload = () => {
  return useSelector((state) => state.searchFilters?.auxPayload);
};

export const useSearchFiltersLayout = () => {
  return useSelector((state) => state.searchFilters?.filters);
};

export const useSearchFiltersUpdatedTimeLayout = () => {
  return useSelector((state) => state.searchFilters?.updatedTime);
};

export const useValueFromPayload = (sectionName: any, payloadName: any) => {
  return useSelector(
    (state) =>
      state?.searchFilters?.auxPayload?.[sectionName]?.[payloadName?.[0]]
  );
};

export const useValueFromPayloadRange = (
  sectionName: any,
  payloadName: any
) => {
  const payload = useSearchFiltersAuxPayload();

  return [
    payload?.[sectionName]?.[payloadName?.[0]],
    payload?.[sectionName]?.[payloadName?.[1]],
  ];
};

export const useValueFromPayloadRangeOrSingleValue = (
  sectionName: any,
  payloadName: any,
  isRange: boolean,
  parentKey?: string
) => {
  const payload = useSearchFiltersAuxPayload();
  const singleValue = useValueFromPayload(sectionName, payloadName);

  if (isRange) {
    if (parentKey) {
      const key = Object.keys(payloadName)[0];
      return [
        payload?.[sectionName]?.[parentKey]?.[key]?.[payloadName?.[key]?.[0]],
        payload?.[sectionName]?.[parentKey]?.[key]?.[payloadName?.[key]?.[1]],
      ];
    }
    return [
      payload?.[sectionName]?.[payloadName?.[0]],
      payload?.[sectionName]?.[payloadName?.[1]],
    ];
  } else return singleValue;
};

export const useSelectedFilterTab = () => {
  return useSelector(
    (state) => state.searchFilters?.selectedFilterTab ?? "classic_search"
  );
};
